import type { MemoryState, PlayerIndex } from "@playpal/core";

interface Props {
	state: MemoryState;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

const OWNER_RING = ["ring-rose-400", "ring-sky-400"] as const;

export function MemoryBoard({ state, seat, onMove }: Props) {
	const frozen = state.hideAt !== null;
	const myTurn = state.turn === seat && !state.result && !frozen;

	return (
		<div className="board-frame board-lg grid grid-cols-4 gap-2 sm:gap-3">
			{state.cards.map((card, index) => {
				const showing = card.owner !== null || state.faceUp.includes(index);
				const matched = state.lastMatch?.includes(index) ?? false;
				return (
					<button
						// A dealt deck never reorders, so the index is a stable key.
						key={index}
						type="button"
						disabled={!myTurn || showing}
						aria-label={showing ? `Card showing ${card.emoji}` : `Card ${index + 1}, face down`}
						onClick={() => onMove({ card: index })}
						className={`aspect-square rounded-2xl text-4xl shadow-md transition [perspective:800px] sm:text-5xl ${
							showing ? "" : "enabled:hover:scale-105"
						} ${card.owner !== null ? "opacity-70" : ""} ${matched ? "animate-pop" : ""}`}
					>
						{/*
						 * A real turn: two faces on a cube, not a rotating label. The
						 * back of the card used not to exist in the DOM at all, so
						 * "flipping" was the emoji spinning on the spot.
						 */}
						<span className="flip" data-face={showing ? "back" : "front"} aria-hidden="true">
							<span className="flip-face rounded-2xl bg-indigo-500 text-indigo-200">?</span>
							<span
								className={`flip-face flip-face-back rounded-2xl bg-white ring-4 ring-inset dark:bg-white/90 ${
									card.owner === null ? "ring-amber-300" : OWNER_RING[card.owner]
								}`}
							>
								{card.emoji}
							</span>
						</span>
					</button>
				);
			})}
		</div>
	);
}
